import { useMemo, useState } from 'react';

import { updateAssetBatchReprocess, ReprocessPayload } from 'src/api/functions/assetsApi';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import { useWritesPaused } from 'src/hooks/useWritesPaused';

import {
  buildOutputGroups,
  EMPTY_SELECTION,
  pruneSelection,
  ReprocessSelection,
  selectionCount,
  TypeCounts,
} from './reprocessOptions';
import { ReprocessStep } from './StepIndicator';
import { useReprocessEstimate } from './useReprocessEstimate';

/**
 * State shared by the bulk-action modal and the admin page: the step, the
 * outputs picked, the live estimate and the submit. `counts` are the image /
 * video counts when the caller knows them (the asset grid does; pasted ids do
 * not until the estimate answers).
 */
export const useReprocessFlow = (ids: string[], knownCounts?: TypeCounts) => {
  const { data: settings, isLoading: loadingSettings } = useSettingsQuery();
  const accessToken = useAccessToken();
  const { showAlert, showSuccess } = useAlerts();
  // A trial without a card, an expired or over-quota account, or one at its
  // spend limit: reprocess would 503, so say why up front (#1018 dev test).
  const { paused: writesPaused, reason: pausedReason } = useWritesPaused();

  const [step, setStep] = useState<ReprocessStep>('assets');
  const [rawSelection, setSelection] = useState<ReprocessSelection>(EMPTY_SELECTION);
  const [submitting, setSubmitting] = useState(false);

  // Types for pasted ids come from an outputs-agnostic estimate.
  const probe = useReprocessEstimate(
    { ids, conversions: ['smart_imaging'], transcodes: [], addons: [] },
    !knownCounts && ids.length > 0,
  );
  const counts: TypeCounts | undefined =
    knownCounts ?? (probe.data ? { image: probe.data.counts.image, video: probe.data.counts.video } : undefined);

  const groups = useMemo(() => buildOutputGroups(settings, counts), [settings, counts?.image, counts?.video]);
  const selection = useMemo(() => pruneSelection(rawSelection, groups), [rawSelection, groups]);

  const payload: ReprocessPayload = useMemo(() => ({ ids, ...selection }), [ids, selection]);
  const estimate = useReprocessEstimate(payload, step === 'review' && selectionCount(selection) > 0);

  // The reprocess API refuses the whole batch over one unknown / unreachable
  // id, so send only what the estimate could reach.
  const submitIds = useMemo(() => {
    const skipped = new Set((estimate.data?.skipped ?? []).map((s) => s.id));
    return ids.filter((id) => !skipped.has(id));
  }, [ids, estimate.data]);

  const submit = async (): Promise<boolean> => {
    if (submitting || !submitIds.length) return false;
    setSubmitting(true);
    try {
      await updateAssetBatchReprocess({ ...payload, ids: submitIds }, accessToken);
      const n = submitIds.length;
      showSuccess(`Reprocessing queued for ${n} asset${n === 1 ? '' : 's'}. Video progress is on the Jobs page.`);
      return true;
    } catch (e: any) {
      if (e?.status === 402) {
        showAlert(
          `Not enough credits: this batch needs ${e.body?.required ?? '?'} and the balance is ${e.body?.balance ?? '?'}.`,
        );
      } else if (e?.status === 503) {
        showAlert('Reprocessing is paused for this account. See the notice above for how to resume it.');
      } else if (e?.status === 403) {
        showAlert('You do not have permission to reprocess these assets.');
      } else {
        showAlert(e?.message || 'Reprocess request failed. Contact support if this persists.');
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    step,
    setStep,
    groups,
    selection,
    setSelection,
    counts,
    loadingSettings,
    probe,
    estimate,
    submitting,
    submit,
    submitIds,
    writesPaused,
    pausedReason,
    canSubmit: !writesPaused && !submitting && submitIds.length > 0 && selectionCount(selection) > 0 && estimate.data?.sufficient !== false,
  };
};

export default useReprocessFlow;
